import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { clearOptionsCache } from '../api/optionsCache';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('portal_token');
    const storedUser = localStorage.getItem('portal_user');
    
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
      }
    }
    
    setLoading(false);
  }, []);

  // The filter-option lists (LOBs, functions, branches) are scoped to the
  // signed-in user and cached for the session. Signing in or out does NOT
  // reload the page, so the cache has to be dropped here or the next user in
  // this tab inherits the previous one's LOB names.
  const login = async (email, password) => {
    const response = await authApi.login(email, password);

    localStorage.setItem('portal_token', response.token);
    localStorage.setItem('portal_user', JSON.stringify(response.user));

    clearOptionsCache();
    setUser(response.user);
    navigate('/dashboard');
  };

  const loginWithToken = (token, userData) => {
    localStorage.setItem('portal_token', token);
    localStorage.setItem('portal_user', JSON.stringify(userData));
    clearOptionsCache();
    setUser(userData);
  };

  const logout = async () => {
    await authApi.logout();
    clearOptionsCache();
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
